import React from 'react'
import PropTypes from 'prop-types'
import {Button, Input, Select} from 'antd'
import {formatDateTime} from '../../helpers/format'

const {Option} = Select

/**
 * Thanh lưu / nạp lại cấu hình dùng chung — dùng cho CẢ tham số nâng cao lẫn tỷ lệ mức khẩn.
 *
 * Gom thành một component thay vì chép hai lần: hai khối này giống nhau tới từng nhãn, mà mỗi lần
 * sửa một bên rồi quên bên kia là người dùng gặp hai hành vi khác nhau cho cùng một việc.
 *
 * Ba nguyên tắc giữ nguyên ở cả hai chỗ:
 *   · Mặc định KHÔNG lưu — phải tích ô mới bật được nút, vì đây là cấu hình dùng chung cả nhà máy.
 *   · Tên TUỲ CHỌN, không điền sẵn — danh sách vẫn nhận diện được bản lưu bằng người + thời điểm.
 *   · "Đặt lại" chỉ đưa MÀN HÌNH về bản đã lưu, không đụng gì tới server.
 */
function ConfigSaveBar({
    enabled, onEnabledChange, name, onNameChange, onSave, saving, onReset,
    meta, emptyText, history, onLoad, disabled,
}) {
    return (
        <div className="VcUrgencyConfigSave">
            <label className="VcUrgencyConfigSave__toggle">
                <input
                    type="checkbox"
                    checked={enabled}
                    disabled={disabled}
                    onChange={(e) => onEnabledChange(e.target.checked)}
                />
                Lưu cấu hình này cho lần sau (áp dụng cho mọi người dùng)
            </label>

            {/*
              * Ô tên chỉ hiện khi đã tích lưu: chưa định lưu thì nó là một ô trống vô nghĩa chiếm chỗ.
              * `Input` để trống được — nút Lưu tự khoá, kèm câu nhắc, thay vì tự điền lại ngầm.
              */}
            {enabled && (
                <Input
                    size="small"
                    style={{width: 260}}
                    value={name}
                    maxLength={120}
                    placeholder="Tên cấu hình (tuỳ chọn)"
                    onChange={(e) => onNameChange(e.target.value)}
                />
            )}

            <Button
                size="small"
                type="primary"
                loading={saving}
                disabled={!enabled || saving}
                onClick={onSave}
            >
                Lưu cấu hình
            </Button>

            <Button
                size="small"
                disabled={saving}
                onClick={onReset}
                title="Đưa màn hình về đúng cấu hình đã lưu. Không đụng gì tới cấu hình dùng chung."
            >
                Đặt lại
            </Button>

            {/*
              * Nạp lại một bản đã lưu = chỉ ÁP LÊN MÀN HÌNH, không tự ghi xuống server. Muốn bản đó
              * thành cấu hình chung thì vẫn phải tích ô và bấm Lưu — nạp nhầm một bản cũ không được
              * phép đổi cấu hình của cả nhà máy chỉ vì một cú bấm chọn.
              */}
            {history.length > 0 && (
                <Select
                    size="small"
                    style={{minWidth: 300}}
                    placeholder={`Nạp lại cấu hình đã lưu (${history.length})`}
                    value={undefined}
                    /*
                     * Tìm kiếm được: danh sách giữ tới 100 bản, cuộn tay để tìm "kịch bản 2 ca" là
                     * việc không ai chịu làm. `optionFilterProp="label"` để lọc theo đúng chuỗi đang
                     * hiện — lọc theo `value` sẽ là lọc theo id, tức gõ gì cũng không ra.
                     */
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                        String(option.label || '').toLowerCase().includes(String(input).toLowerCase())}
                    onChange={(id) => {
                        const entry = history.find((item) => item.id === id)

                        if (entry) onLoad(entry)
                    }}
                >
                    {history.map((item) => {
                        // Không đặt tên vẫn phải phân biệt được — dùng người lưu và thời điểm.
                        const label = [item.name, item.createdBy, formatDateTime(item.createdAt)]
                            .filter(Boolean)
                            .join(' · ')

                        return (
                            <Option key={item.id} value={item.id} label={label}>
                                {label}
                            </Option>
                        )
                    })}
                </Select>
            )}

            <div className="VcConfigSaved">
                {meta.savedBy
                    ? `Cấu hình đang lưu do ${meta.savedBy} đặt${meta.savedAt ? ` lúc ${formatDateTime(meta.savedAt)}` : ''}.`
                    : emptyText}
            </div>
        </div>
    )
}

ConfigSaveBar.propTypes = {
    enabled: PropTypes.bool.isRequired,
    onEnabledChange: PropTypes.func.isRequired,
    name: PropTypes.string,
    onNameChange: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    saving: PropTypes.bool,
    onReset: PropTypes.func.isRequired,
    meta: PropTypes.object,
    emptyText: PropTypes.string.isRequired,
    history: PropTypes.array,
    onLoad: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
}

ConfigSaveBar.defaultProps = {
    name: '',
    saving: false,
    meta: {savedBy: null, savedAt: null},
    history: [],
    disabled: false,
}

export default ConfigSaveBar
